import React from "react";
import { ModeRendererProps } from "./types";

// VASARELY × RICHARD MILLE: Mechanical transparency
// Gear lattices, skeleton frames, carbon weave
export function VasarelyRichardmilleMode({ brand }: ModeRendererProps) {
  const rmGray = "#808080";
  const rmCarbon = "#1A1A1A";

  return (
    <g>
      {/* Carbon fiber background */}
      <defs>
        <pattern id="carbon-weave" x="0" y="0" width="20" height="20" patternUnits="userSpaceOnUse">
          <rect x="0" y="0" width="10" height="10" fill="#1A1A1A" />
          <rect x="10" y="10" width="10" height="10" fill="#1A1A1A" />
          <rect x="10" y="0" width="10" height="10" fill="#2A2A2A" />
          <rect x="0" y="10" width="10" height="10" fill="#2A2A2A" />
        </pattern>
      </defs>

      <rect x="0" y="0" width="794" height="1123" fill="url(#carbon-weave)" />

      <g transform="translate(397 540)">
        {/* Skeleton frame (barrel shape) */}
        <rect x="-150" y="-200" width="300" height="400" fill="none" stroke={rmGray} strokeWidth="4" opacity="0.7" rx="40" />
        <rect x="-140" y="-190" width="280" height="380" fill="none" stroke={rmGray} strokeWidth="2" opacity="0.5" rx="35" />

        {/* Gear lattices */}
        {[
          { x: 0, y: -100, teeth: 20, r: 50 },
          { x: -80, y: 60, teeth: 16, r: 40 },
          { x: 80, y: 60, teeth: 16, r: 40 },
        ].map((gear, gearIndex) => (
          <g key={gearIndex}>
            {/* Gear body */}
            <circle
              cx={gear.x}
              cy={gear.y}
              r={gear.r}
              fill={rmCarbon}
              opacity="0.8"
            />
            <circle
              cx={gear.x}
              cy={gear.y}
              r={gear.r}
              fill="none"
              stroke={rmGray}
              strokeWidth="2"
              opacity="0.7"
            />
            
            {/* Gear teeth */}
            {Array.from({ length: gear.teeth }).map((_, i) => {
              const angle = (i * Math.PI * 2) / gear.teeth;
              const toothR1 = gear.r;
              const toothR2 = gear.r + 8;
              
              return (
                <line
                  key={i}
                  x1={gear.x + Math.cos(angle) * toothR1}
                  y1={gear.y + Math.sin(angle) * toothR1}
                  x2={gear.x + Math.cos(angle) * toothR2}
                  y2={gear.y + Math.sin(angle) * toothR2}
                  stroke={rmGray}
                  strokeWidth="3"
                  opacity="0.8"
                />
              );
            })}
            
            {/* Center hub */}
            <circle
              cx={gear.x}
              cy={gear.y}
              r={gear.r * 0.3}
              fill={rmGray}
              opacity="0.6"
            />
            
            {/* Spokes */}
            {[0, 72, 144, 216, 288].map((angle) => {
              const rad = (angle * Math.PI) / 180;
              return (
                <line
                  key={angle}
                  x1={gear.x}
                  y1={gear.y}
                  x2={gear.x + Math.cos(rad) * gear.r * 0.8}
                  y2={gear.y + Math.sin(rad) * gear.r * 0.8}
                  stroke={rmGray}
                  strokeWidth="2"
                  opacity="0.5"
                />
              );
            })}
          </g>
        ))}

        {/* Transparency lattice */}
        {Array.from({ length: 12 }).map((_, i) =>
          Array.from({ length: 16 }).map((_, j) => {
            const x = (i - 5.5) * 48;
            const y = (j - 7.5) * 42;
            
            return (
              <rect
                key={`${i}-${j}`}
                x={x - 20}
                y={y - 18}
                width="40"
                height="36"
                fill="none"
                stroke={rmGray}
                strokeWidth="0.5"
                opacity="0.15"
              />
            );
          })
        )}

        {/* Precision circles */}
        {Array.from({ length: 12 }).map((_, i) => {
          const r = 60 + i * 18;
          return (
            <circle
              key={i}
              cx="0"
              cy="0"
              r={r}
              fill="none"
              stroke={rmGray}
              strokeWidth="1"
              opacity={0.25 - i * 0.015}
            />
          );
        })}

        {/* Screws (design signature) */}
        {[
          { x: -130, y: -180 },
          { x: 130, y: -180 },
          { x: -130, y: 180 },
          { x: 130, y: 180 },
        ].map((screw, i) => (
          <g key={i}>
            <circle cx={screw.x} cy={screw.y} r="8" fill={rmGray} opacity="0.7" />
            <line
              x1={screw.x - 6}
              y1={screw.y}
              x2={screw.x + 6}
              y2={screw.y}
              stroke={rmCarbon}
              strokeWidth="2"
              opacity="0.8"
            />
            <line
              x1={screw.x}
              y1={screw.y - 6}
              x2={screw.x}
              y2={screw.y + 6}
              stroke={rmCarbon}
              strokeWidth="2"
              opacity="0.8"
            />
          </g>
        ))}

        {/* Central tourbillon window */}
        <circle cx="0" cy="0" r="70" fill={rmCarbon} opacity="0.9" />
        <circle cx="0" cy="0" r="70" fill="none" stroke={rmGray} strokeWidth="3" opacity="0.8" />
      </g>

      {/* Richard Mille text */}
      <text x="397" y="1030" textAnchor="middle" fontFamily="monospace" fontSize="11" fill={rmGray} opacity="0.8">
        TRANSPARENCE MÉCANIQUE
      </text>
    </g>
  );
}
